import { desk } from '@isyscore/messenger';
import { objectAssign } from '@isyscore/utils/object';
import App from './app.class';
import store, { RootState } from '@/store/index';
import LocationManager from './location-manager';
import { DeskApp } from '@/types/desk';
import { RouteInfo, RouterInfo, RouterObj } from '@/store/modules/app-manager/types';
import Vue from 'vue';
import { eventBus } from '@/plugins/event-bus';
import { openAlert } from '@/utils/message';

const CURRENT_APP_ID = 'currentAppId';
const OPENED_APP_MAP = 'openedAppMap';
const OPENED_APP_IDS = 'openedAppIds';

type OpenedAppMap = Record<App['instanceId'], App>;

export default class AppManager {
    userAppList: Array<DeskApp>;
    private static _instance: AppManager = new AppManager();
    openedAppMap: OpenedAppMap;

    constructor() {
        if (AppManager._instance) {
            throw new Error('Error: Instantiation failed: Use AppManager.getInstance() instead of new.');
        }

        this.userAppList = [];
        this.openedAppMap = {};
        this._initStoreListeners();
        this._recover();
        this._initListeners();
        AppManager._instance = this;
    }

    static getInstance(): AppManager {
        return AppManager._instance;
    }

    get currentAppId(): number {
        return store.state.appManager.currentAppId;
    }

    set currentAppId(val: number) {
        store.commit('appManager/setCurrentAppId', val);
    }

    get openedAppIds(): Array<number> {
        return store.state.appManager.openedAppIds;
    }

    set openedAppIds(val: number[]) {
        store.commit('appManager/setOpenedAppIds', val);
    }

    _updateUserAppList(arr: Array<DeskApp>): void {
        if (!Array.isArray(arr)) {
            throw new Error('injectUserAppList 必须传入数组参数');
        }

        const cloneArr: Array<DeskApp> = objectAssign([], arr);

        // 兼容新老两代孪生模型放进去
        cloneArr.push({
            id: 99999,
            code: 'twin-model',
            version: '1.3.0',
            name: '孪生模型',
            logo: '/desktop/icon/vis.png',
            type: 0,
            secondType: 5,
            status: 1,
            inMenu: 1,
            surName: 'L'
        });
        this.userAppList = cloneArr.map((item) => Object.freeze(item));
    }

    /**
     * 处理页面刷新和从链接跳转进入页面
     */
    _recover(): void {
        try {
            if (!this._getAppCodeFromLocation()) {
                if (!desk.sessionStorage.getItem(CURRENT_APP_ID) || !desk.sessionStorage.getItem(OPENED_APP_IDS)) {
                    return;
                }
                this._recoverFromReload();
                return;
            }

            // 1. 处理跳转进来的逻辑
            if (!desk.sessionStorage.getItem(CURRENT_APP_ID) || !desk.sessionStorage.getItem(OPENED_APP_IDS)) {
                void this._recoverFromJump();
                return;
            }

            // 有缓存记录，当前桌面正处于打开状态
            if (this._testIsUrlAppSameWithCacheApp()) {
                // 3. 地址栏里的当前app 和未刷新之前的 app 一致
                this._updateCacheFromUrl();
                this._recoverFromReload();
            } else {
                // 2. 只要桌面当前的 currentApp 的 code 和 地址栏的 app code 不一致，就打开新 tab
                this._recoverFromJumpAndAdd();
            }
        } catch (e) {
            // ignorez
        }
    }

    /**
     * 判断 url 里边的 current app 和 url 里边的 app 是否相同
     */
    _testIsUrlAppSameWithCacheCurrentApp(): boolean {
        const cacheCurrentApp = this._getCurrentCacheApp();
        const cacheCurrentAppCode = cacheCurrentApp._configs.code;
        return this._getAppCodeFromLocation() === cacheCurrentAppCode;
    }

    _getCurrentCacheApp(): App {
        const cacheAppMap = JSON.parse(desk.sessionStorage.getItem(OPENED_APP_MAP)!) as Record<App['instanceId'], App>;
        const cacheAppId = desk.sessionStorage.getItem(CURRENT_APP_ID);
        const cacheAppIdNum = Number(cacheAppId);
        return cacheAppMap[cacheAppIdNum];
    }

    _updateCacheFromUrl(): void {
        const cacheAppMap = JSON.parse(desk.sessionStorage.getItem(OPENED_APP_MAP)!) as Record<App['instanceId'], App>;
        const cacheApp = this._getCurrentCacheApp();
        const newRouterInfo = this._genRouteObjFromLocation();
        cacheApp.routeInfo = newRouterInfo.route;
        cacheApp.routerInfo = newRouterInfo.router;

        cacheAppMap[cacheApp.instanceId] = cacheApp;
        console.log(cacheApp, 'cacheApp');
        desk.sessionStorage.setItem(OPENED_APP_MAP, JSON.stringify(cacheAppMap));
    }

    _genRouteObjFromLocation(): RouterObj {
        const sp = new URLSearchParams(location.search);
        if (sp.has('appT') && sp.has('appU') && sp.has('appN')) {
            return {
                router: {
                    type: sp.get('appT') as string,
                    base: `/os/${sp.get('appN') as string}`
                },
                route: {
                    fullPath: sp.get('appU') as string
                }
            };
        } else {
            return {
                router: {} as RouterInfo,
                route: {} as RouteInfo
            };
        }
    }

    /**
     * 从 url 跳转过来的时候看一下，url 里边的 app 在缓存中是否已经打开
     * return {boolean} true 只有已打开应用列表存在，且地址栏应用不再列表中返回 true
     */
    _testIsUrlAppSameWithCacheApp(): boolean {
        const appCode = this._getAppCodeFromLocation();
        const cacheCurrentApp = this._getCurrentCacheApp();
        return cacheCurrentApp._configs.code === appCode;
        // if (!desk.sessionStorage.getItem(OPENED_APP_MAP)) return false;
        // const appMap = JSON.parse(desk.sessionStorage.getItem(OPENED_APP_MAP)!) as Record<App['instanceId'], App>;
        // if (!appCode) return false;
        // return !Object.keys(appMap).some((appId) => {
        //     const app = appMap[Number(appId)];
        //     return app._configs.code === appCode;
        // });
    }

    /**
     * 刷新页面的时候，恢复 app-manager 状态
     */
    _recoverFromReload(): void {
        if (!desk.sessionStorage.getItem(OPENED_APP_MAP)) return;

        const lastCurrentAppId = Number(desk.sessionStorage.getItem(CURRENT_APP_ID)) || -1;

        const openedAppMapString = desk.sessionStorage.getItem(OPENED_APP_MAP);
        const openedAppIdString = desk.sessionStorage.getItem(OPENED_APP_IDS);

        if (!openedAppMapString || !openedAppIdString) {
            console.error('_recoverFromReload 无法获取已打开的 app 信息');
            return;
        }

        const configMaps: OpenedAppMap = JSON.parse(openedAppMapString) as OpenedAppMap;
        const oldOpenedAppIs: number[] = JSON.parse(openedAppIdString) as number[];

        const [newOpenedAppMap, newCurrentAppId, newAppIds] = this._genNewAppsFromConfig(
            oldOpenedAppIs,
            configMaps,
            lastCurrentAppId
        );

        // 这个地方由于 下面 watch vue store 和 页面iframe 地址重复刷新问题
        // 必须先设置 openedAppMap， 再设置 newOpenAppIds
        this.openedAppMap = newOpenedAppMap;
        this.openedAppIds = newAppIds;
        this.currentAppId = newCurrentAppId;
    }

    _genNewAppsFromConfig(
        oldOpenedAppIs: number[],
        configMaps: OpenedAppMap,
        lastCurrentAppId: number
    ): [OpenedAppMap, App['instanceId'], number[]] {
        const newAppIds: number[] = [];
        const newOpenedAppMap: OpenedAppMap = {};
        let newCurrentAppId = -1;
        for (const key of oldOpenedAppIs) {
            const appConfig = configMaps[key];

            if (!this._testAppCanVisit(appConfig._configs.code)) {
                continue;
            }

            const { instanceId, routerInfo, routeInfo, _configs } = appConfig;
            let app: App;
            if (Number(instanceId) === Number(lastCurrentAppId)) {
                app = new App(_configs, routerInfo, routeInfo);
                newCurrentAppId = app.instanceId;
            } else {
                app = new App(_configs, routerInfo, routeInfo);
            }
            newAppIds.push(app.instanceId);
            newOpenedAppMap[app.instanceId] = app;
        }

        return [newOpenedAppMap, newCurrentAppId, newAppIds];
    }

    /**
     * 从跳转的页面刷新
     */
    _recoverFromJump(): void {
        const searchParams = new URLSearchParams(location.search);
        const appN = searchParams.get('appN') || this._getAppCode() || '';

        if (!appN) return;

        if (!this._testAppCanVisit(appN)) return;

        const appConfig = this.userAppList.find((item) => item.code === appN) as DeskApp;

        const mockRouterInfo: RouterInfo = {
            type: searchParams.get('appT') as string,
            base: ''
        };

        const mockRouteInfo: RouteInfo = {
            fullPath: searchParams.get('appU') as string
        };
        const app = new App(appConfig, mockRouterInfo, mockRouteInfo);

        this.openedAppMap[app.instanceId] = app;
        this.openedAppIds.push(app.instanceId);
        this.currentAppId = app.instanceId;
    }

    /**
     * 页面跳转打开一个之前没有的 app
     */
    _recoverFromJumpAndAdd(): void {
        const sp = new URLSearchParams(location.search);
        const appCode = this._getAppCodeFromLocation();
        if (!sp.has('appT') || !sp.has('appU')) {
            this._recoverFromReload();
            this.openApp(appCode);
        } else {
            const appT = sp.get('appT') || '';
            const appU = sp.get('appU') || '';
            const appN = sp.get('appN') || appCode;
            const path = appT === 'hash' ? `#${appU}` : appU;
            this._recoverFromReload();
            this.openApp(appN, path);
        }
    }

    _initListeners(): void {
        const { receive } = desk;
        receive('navigator:url', (payload: RouterObj | { url: string; base: string; type: string }) =>
            this._onAppRouteUpdate(payload)
        );
    }

    _onAppRouteUpdate(payload: RouterObj | { url: string; base: string; type: string }): void {
        if (!('route' in payload)) {
            // 兼容一下老的 messenger
            const mockRouterObj = {
                route: {
                    fullPath: payload.url
                },
                router: {
                    base: payload.base,
                    type: payload.type
                }
            };
            this.updateDesktopUrl(mockRouterObj);
        } else {
            this.updateDesktopUrl(payload);
        }
    }

    /**
     * 打开app
     * @param {*} appCode
     * @param {string} to 可选，打开应用，并跳转到指定页面，子应用 fullPath，hash 和 history 自己传入
     */
    openApp(appCode: string, to = ''): void {
        if (!this._testAppCanVisit(appCode)) return;

        const app = this._genNewApp(appCode, to);

        LocationManager.updateDesktopUrlByApp(app);
        this.openedAppMap[app.instanceId] = app;
        this.openedAppIds.push(app.instanceId);
        this.currentAppId = app.instanceId;

        if (app.outsideAppTag) {
            setTimeout(() => {
                window.open(app._configs.redirectUrl);
            }, 400);
            // window.open(app._configs.redirectUrl);
        }

        // this.clearUrlParams(); // 清除掉当前桌面 url 参数，为了新 app 计算
    }

    _genNewApp(appCode: string, to = ''): App {
        const config = this.userAppList.find((item) => item.code === appCode) as DeskApp;
        let app: App | null;
        if (to) {
            const mockRouterObj = this._genMockRouterObj(to);
            app = new App(config, mockRouterObj.router, mockRouterObj.route);
        } else {
            app = new App(config, {}, {});
        }

        return app;
    }

    _genMockRouterObj(url: string): RouterObj {
        return {
            router: {
                type: url.startsWith('#') ? 'hash' : 'history',
                base: ''
            },
            route: {
                fullPath: url.startsWith('#') ? url.substring(1) : url
            }
        };
    }

    /**
     * 激活 app，tab 标签切换
     * @param {number} appId
     */
    activeApp(appId: number): void {
        if (this.currentAppId === appId) return;
        if (!this.openedAppIds.includes(appId)) return;

        const app = this.openedAppMap[appId];
        this.currentAppId = appId;
        LocationManager.updateDesktopUrlWhenChangeApp(app);
    }

    /**
     * 切换到桌面，所有应用放在后台
     */
    hideApps(): void {
        this.currentAppId = -1;
    }

    /**
     * 用户是否有权限访问指定应用
     * @param {string} appCode
     * @returns {boolean} 用户是否有使用某应用的权限
     */
    _testAppCanVisit(appCode: string): boolean {
        const app = this.userAppList.find((app) => app.code === appCode);

        if (app) {
            if (App.isAppUrlRight(app)) {
                return true;
            }
            void openAlert(`${app.name} 路径参数配置不正确，请联系管理员！`).then();
            return false;
        } else {
            void openAlert(`您没有权限打开 ${appCode}，或者  ${appCode} 应用已被停用，请联系管理员！`).then();
            return false;
        }
    }

    /**
     * 指定应用是不是正处于打开状态
     * @param {number} appId
     * @returns {boolean} 应用是否正处于打开状态
     */
    testAppOpening(appId: number): boolean {
        return appId in this.openedAppMap;
    }

    /**
     * 通过 appId 获取应用的实例
     * @param {number} appId
     * @returns App | null
     */
    _getAppById(appId: number): App | null {
        const app = this.openedAppMap[appId];
        if (app) {
            return app;
        } else {
            return null;
        }
    }

    /**
     * 替换当前 app
     * @param appCode
     * @param to
     */
    replaceApp(appCode: string, to: string): void {
        if (!this._testAppCanVisit(appCode)) return;
        if (this.currentAppId === -1) return;

        const app = this._genNewApp(appCode, to);
        app.setInstanceId(this.currentAppId);

        LocationManager.updateDesktopUrlByApp(app);

        // this.openedAppMap[this.currentAppId] = app;
        Vue.set(this.openedAppMap, this.currentAppId, app);
        (eventBus as Vue).$emit('rewrite-app', app);

        if (app.outsideAppTag) {
            setTimeout(() => {
                window.open(app._configs.redirectUrl);
            }, 2000);
        }
    }

    appLoaded(appId: number): void {
        // ....
        const app = this._getAppById(appId);
        if (app) {
            app.loaded = true;
        }
    }

    /**
     * 通过 appId 关闭 app 的 tab 标签
     * @param {number} appId
     */
    closeApp(appId: number): void {
        if (!this.openedAppIds.length || !this.testAppOpening(appId)) {
            console.error('try to close an un-existing app');
            return;
        }
        if (this.openedAppIds.length === 1) {
            this.currentAppId = -1;
            delete this.openedAppMap[appId];
            this.openedAppIds = [];
        } else {
            const toCloseIndex = this.openedAppIds.findIndex((item) => item === appId);
            this.openedAppIds.splice(toCloseIndex, 1);

            if (this.currentAppId === appId) {
                this.currentAppId = this.openedAppIds[toCloseIndex + 1]
                    ? this.openedAppIds[toCloseIndex + 1]
                    : this.openedAppIds[toCloseIndex - 1];
            }

            delete this.openedAppMap[appId];
        }
        const currentApp = this.openedAppMap[this.currentAppId];
        if (currentApp) {
            LocationManager.updateDesktopUrlByApp(currentApp);
        } else {
            LocationManager.updateDesktopUrlWhenNoApp();
        }
    }

    updateDesktopUrl(routerObj: RouterObj): void {
        const app = this._getAppById(this.currentAppId);
        if (!app) return;
        app.updateRouteInfo(routerObj);
        LocationManager.updateDesktopUrlWhenRouteChange(app, routerObj);
        desk.sessionStorage.setItem(OPENED_APP_MAP, JSON.stringify(this.openedAppMap));
    }

    _initStoreListeners(): void {
        store.watch(
            (state: RootState) => state.desk.allowedApps,
            (newVal) => {
                this._updateUserAppList(newVal);
            }
        );

        store.watch(
            (state: RootState) => state.appManager.currentAppId,
            (newVal, oldVal) => {
                if (newVal > 0) {
                    const app = this._getAppById(newVal);
                    document.title = app ? app._configs.name : '桌面';
                } else {
                    document.title = '桌面';
                }
                noticeIfames(newVal, oldVal);
                desk.sessionStorage.setItem('currentAppId', String(newVal));

                function noticeIfames(newCurrentAppId: number, oldCurrentAppId: number) {
                    [newCurrentAppId, oldCurrentAppId]
                        .filter((item) => item > 0)
                        .forEach((id) => {
                            const iframe = document.getElementById(`app-runner-${id}`) as HTMLIFrameElement;
                            if (iframe) {
                                const target = iframe.contentWindow;
                                if (id === newCurrentAppId) {
                                    target?.postMessage('isc-desktop-app:active');
                                }
                                if (id === oldCurrentAppId) {
                                    target?.postMessage('isc-desktop-app:deactive');
                                }
                            }
                        });
                }
            }
        );
        store.watch(
            (state: RootState) => state.appManager.openedAppIds,
            (newVal) => {
                desk.sessionStorage.setItem(OPENED_APP_IDS, JSON.stringify(newVal));
                desk.sessionStorage.setItem(OPENED_APP_MAP, JSON.stringify(this.openedAppMap));
                if (!newVal || !newVal.length) LocationManager.updateDesktopUrlWhenNoApp();
            }
        );
        this._updateUserAppList(store.state.desk.allowedApps);
    }

    _getAppCodeFromLocation(): string {
        const pathnames = location.pathname.split(/\//).filter((s) => !!s);
        if (!pathnames || !pathnames.length) return '';
        const words = ['desktop', 'homepage', 'os', 'app'];
        const lastPath = pathnames[pathnames.length - 1];
        if (words.includes(lastPath)) {
            return '';
        } else {
            return lastPath;
        }
    }

    _getAppCode() {
        const pathnames = location.pathname.split(/\//).filter((s) => !!s);
        if (pathnames && pathnames.length) return pathnames[pathnames.length - 1];
        return '';
    }
}
